import type { FieldValues, Path } from 'react-hook-form';

/**
 * ValidationError class for form validation errors
 *
 * Supports two formats:
 * 1. New API format: { message, fields: Record<string, string[]> }
 * 2. Legacy format: { message, fieldErrors, formErrors } for backward compatibility
 */
export class ValidationError<TFieldValues extends FieldValues = FieldValues> extends Error {
  public fieldErrors: Record<Path<TFieldValues>, string[]>;
  public formErrors: string[];

  constructor(
    message: string,
    fieldsOrFieldErrors: Record<Path<TFieldValues>, string[]> | Record<string, string[]>,
    formErrors?: string[]
  ) {
    super(message);
    this.name = 'ValidationError';

    // New format: { message, fields } - formErrors is undefined
    if (formErrors === undefined) {
      // Convert fields to fieldErrors (they're the same, just named differently)
      this.fieldErrors = fieldsOrFieldErrors as Record<Path<TFieldValues>, string[]>;
      // Extract form-level error from message if no specific field errors
      this.formErrors = Object.keys(fieldsOrFieldErrors).length === 0 ? [message] : [];
    } else {
      // Legacy format: { message, fieldErrors, formErrors }
      this.fieldErrors = fieldsOrFieldErrors as Record<Path<TFieldValues>, string[]>;
      this.formErrors = formErrors;
    }
  }

  /**
   * Create ValidationError from new API response format
   * @param apiError - Error response from API: { error: string, fields?: Record<string, string[]> }
   */
  static fromApiError<T extends FieldValues = FieldValues>(apiError: {
    error: string;
    fields?: Record<string, string[]>;
  }): ValidationError<T> {
    return new ValidationError<T>(apiError.error, (apiError.fields || {}) as Record<Path<T>, string[]>);
  }
}
